import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { ConfirmModal, AlertModal } from "../../components/ConfirmModal";

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  max_devices: number;
}

interface Device {
  id: string;
  fingerprint: string;
  device_name: string | null;
  approved: boolean;
  created_at: string;
}

export function DeviceAccessTab() {
  const [clients, setClients] = useState<Profile[]>([]);
  const [devices, setDevices] = useState<Record<string, Device[]>>({});
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    action: () => {},
  });

  const [alert, setAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: "success" | "danger" | "info";
  }>({
    open: false,
    title: "",
    message: "",
    variant: "info",
  });

  useEffect(() => {
    loadSuspendedClients();
  }, []);

  async function loadSuspendedClients() {
    setLoading(true);

    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, email, max_devices")
      .eq("status", "suspended")
      .eq("role", "client");

    if (error) {
      setLoading(false);
      return;
    }

    setClients(data || []);

    const deviceMap: Record<string, Device[]> = {};

    for (const client of data || []) {
      const { data: deviceData } = await supabase
        .from("devices")
        .select("*")
        .eq("user_id", client.id);

      deviceMap[client.id] = deviceData || [];
    }

    setDevices(deviceMap);
    setLoading(false);
  }

  function toggleSelect(clientId: string, deviceId: string) {
    setSelected((prev) => {
      const current = prev[clientId] || [];
      if (current.includes(deviceId)) {
        return {
          ...prev,
          [clientId]: current.filter((id) => id !== deviceId),
        };
      }
      return {
        ...prev,
        [clientId]: [...current, deviceId],
      };
    });
  }

  async function restoreSingle(clientId: string) {
  const selectedDevices = selected[clientId] || [];

  if (selectedDevices.length !== 1) {
    setAlert({
      open: true,
      title: "Selection Required",
      message: "Select exactly one device.",
      variant: "danger",
    });
    return;
  }

  const keepId = selectedDevices[0];

  const { error: deleteError } = await supabase
    .from("devices")
    .delete()
    .eq("user_id", clientId)
    .neq("id", keepId);

  console.log("Delete error:", deleteError);

  const { error: approveError } = await supabase
    .from("devices")
    .update({ approved: true })
    .eq("id", keepId);

  console.log("Approve error:", approveError);

  const { error: profileError } = await supabase
    .from("profiles")
    .update({ max_devices: 1, status: "active" })
    .eq("id", clientId);

  console.log("Profile update error:", profileError);

  setAlert({
    open: true,
    title: "Success",
    message: "Client restored to single device.",
    variant: "success",
  });

  loadSuspendedClients();
}

  async function approveMultiple(clientId: string) {
    const selectedDevices = selected[clientId] || [];

    if (selectedDevices.length !== 2) {
      setAlert({
        open: true,
        title: "Selection Required",
        message: "Select exactly two devices.",
        variant: "danger",
      });
      return;
    }

    await supabase
      .from("devices")
      .update({ approved: true })
      .in("id", selectedDevices);

    await supabase
  .from("devices")
  .delete()
  .eq("user_id", clientId)
  .not("id", "in", selectedDevices);

    await supabase
      .from("profiles")
      .update({ max_devices: 2, status: "active" })
      .eq("id", clientId);

    setAlert({
      open: true,
      title: "Success",
      message: "Multiple device access approved with maximum of 2.",
      variant: "success",
    });

    loadSuspendedClients();
  }

  async function resetDevices(clientId: string) {
  await supabase
    .from("devices")
    .delete()
    .eq("user_id", clientId);

  await supabase
    .from("profiles")
    .update({ max_devices: 1, status: "active" })
    .eq("id", clientId);

  setAlert({
    open: true,
    title: "Devices Reset",
    message: "All devices cleared and reset back to single device. Next device becomes the approved device.",
    variant: "success",
  });

  loadSuspendedClients();
}

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Device Access Management</h1>

      {clients.length === 0 ? (
        <div>No suspended clients.</div>
      ) : (
        clients.map((client) => (
          <div
            key={client.id}
            className="bg-white shadow rounded-xl p-6 space-y-4"
          >
            <div>
              <div className="font-semibold text-lg">
                {client.full_name || "No Name"}
              </div>
              <div className="text-sm text-gray-500">{client.email}</div>
              <div className="text-xs text-gray-400 mt-1">
                Current max devices: {client.max_devices}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2 text-left">Device</th>
                    <th className="px-4 py-2 text-left">Approved</th>
                    <th className="px-4 py-2 text-left">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {devices[client.id]?.map((device) => (
                    <tr key={device.id}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={
                            selected[client.id]?.includes(device.id) || false
                          }
                          onChange={() =>
                            toggleSelect(client.id, device.id)
                          }
                        />
                      </td>
                      <td className="px-4 py-2">
                        {device.device_name || "Unidentified Device"}
                      </td>
                      <td className="px-4 py-2">
                        {device.approved ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-2">
                        {new Date(device.created_at).toLocaleString("en-GB")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => restoreSingle(client.id)}
                className="bg-nhs-blue text-white px-4 py-2 rounded-lg text-sm"
              >
                Restore Single Device
              </button>

              <button
                onClick={() => approveMultiple(client.id)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Approve Multiple (Max 2)
              </button>

              <button
                onClick={() => resetDevices(client.id)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Reset Devices
              </button>
            </div>
          </div>
        ))
      )}

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onClose={() =>
          setAlert((prev) => ({ ...prev, open: false }))
        }
      />
    </div>
  );
}